import i18n from '@/plugins/i18n'
import { getI18n } from '@/plugins/i18n'

import { useLookup } from '@/composables/data-grid'
import { useDefaultDialogTitle } from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'

import { useOrderStore } from '@/stores/order.store'

export const useDialogTitle = () => {
  const { t } = i18n.global
  const { defaultActionTitle } = useDefaultDialogTitle()

  const itemTitle = (i) => i ? `${i.number_mnemonic}/${i.series} ${i.supplier_name}` : ''

  const dialogTitle = (i) => {
    const title = `${defaultActionTitle(i)} ${t('common.supplierOrder')}`
    return i.id ? `${title} - ${itemTitle(i)}` : title
  }

  return { dialogTitle, itemTitle }
}

export const useColumns = (editors, supplierRef, supplierBrandRef) => {
  const i18n = getI18n()

  const orderStore = useOrderStore()

  const { withDocumentSeriesAndNumberMnemonicUpdate: _withUpdate } = dataGridColumn.useDocumentSeriesAndNumberMnemonicUpdate()

  const trackedDocumentIdFields = ['deposit', 'supplier_brand_ref']
  const withDocumentSeriesAndNumberMnemonicUpdate = (fn) =>
    _withUpdate(fn, trackedDocumentIdFields, editors, orderStore.api.getSupplierOrderNextIdByDataAsync)

  const getFilteredSupplierOrderReasons = (options) => {
    let filter = null
    if (supplierBrandRef.value) {
      filter = ['supplier_brand_ref', '=', supplierBrandRef.value]
    }

    return {
      store: orderStore.getLocalizedSupplierOrderReasons(),
      filter: filter,
      sort: 'code',
    }
  }

  return [
    {
      type: 'buttons',
      buttons: [{template: 'editButtonsTemplate'}],
      width: '4rem',
      visibleIndex: 0,
      fixed: false
    },
    dataGridColumn.useAttachmentColumn(),
    { dataField: 'id', caption: i18n.t('common.identifier'), visible: false, showInColumnChooser: false },
    dataGridColumn.useDocumentDateColumn({validationRules: [{ type: 'required' }]}),
    dataGridColumn.useSeriesColumn(),
    dataGridColumn.useNumberMnemonicColumn(),
    dataGridColumn.useSupplierColumn({
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate(async(newData, value, currentData) => {
        await dataGridColumn.useSupplierColumn().setCellValue(newData, value)
        supplierRef.value = newData.supplier_ref
        supplierBrandRef.value = newData.supplier_brand_ref

        newData.supplier_order_reason_ref = null
        newData.urgency_type_ref = null
      }),
      visible: false
    }),
    dataGridColumn.useSupplierDivisionColumn({
      supplierRef,
      editCellTemplate: 'supplierDivisionEditor',
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate((newData, value, currentData) => {
        dataGridColumn.useSupplierDivisionColumn().setCellValue(newData, value)
        supplierBrandRef.value = newData.supplier_brand_ref
        newData.supplier_order_reason_ref = null
      }),
      visible: false,
      showInColumnChooser: false,
    }),
    { dataField: 'supplier_name', caption: i18n.t('common.supplier'), editable: false },
    {
      dataField: 'status',
      caption: i18n.t('common.status'),
      lookup: useLookup({ store: orderStore.getLocalizedOrderStatuses(), displayExpr: 'name' }),
      editable: false
    },
    dataGridColumn.useSupplierBrandColumn({
      validationRules: [{ type: 'required' }],
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate((newData, value) => { newData.supplier_brand_ref = value }),
    }),
    {
      dataField: 'supplier_order_reason_ref',
      dataType: 'number',
      lookup: useLookup({ dataSource: getFilteredSupplierOrderReasons }),
      caption: i18n.t('common.orderReason'),
      visible: false,
      showInColumnChooser: false,
      setCellValue: (newData, value) => {
        newData.urgency_type_ref = null
        newData.supplier_order_reason_ref = value
        const currentOrderReason = orderStore.supplierOrderReasons.find(i => i.id === value)
        if (currentOrderReason && currentOrderReason.urgency_type_ref) {
          newData.urgency_type_ref = currentOrderReason.urgency_type_ref
        }
      },
      validationRules: [{ type: 'required' }]
    },
    dataGridColumn.useUrgencyTypeColumn(),
    dataGridColumn.useDepositColumn({
      visible: false,
      showInColumnChooser: false,
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate((newData, value) => { newData.deposit = value.id }),
    }),
    dataGridColumn.usePaymentTypeColumn({ visible: false, showInColumnChooser: false }),
    dataGridColumn.useDiscountColumn(),
    dataGridColumn.useTotalNoVatColumn(),
    dataGridColumn.useTotalVatColumn(),
    dataGridColumn.useNotesColumn({ visible: false, showInColumnChooser: false }),
    dataGridColumn.useCurrencyColumn({ visible: false, showInColumnChooser: false }),
    dataGridColumn.useExchangeRateColumn({ visible: false, showInColumnChooser: false }),
    dataGridColumn.useExchangeRateDateColumn({ visible: false, showInColumnChooser: false }),
    dataGridColumn.useRequestedDeliveryDateColumn({ visible: false, showInColumnChooser: false }),
  ]
}
